import axiosInstance from './axiosConfig';

/**
 * ムービー制作（段階分離型の動画生成）API クライアント。
 *
 * プロジェクト = シーン配列 + 各シーンの中間生成物（絵コンテ画像 / クリップ動画）。
 * 生成ジョブの進捗は GET /movies/{id} のポーリングだけで追跡する
 * （シーンごとに個別ポーリングしない）。
 */

export type MovieProjectStatus = 'draft' | 'concatenating' | 'completed' | 'concat_failed';

/** シーン段階の状態（採用済み成果物とジョブ状態から導出される） */
export type SceneStageStatus = 'none' | 'pending' | 'processing' | 'completed' | 'failed';

export interface MovieProjectSummary {
  id: string;
  title: string;
  status: MovieProjectStatus;
  aspect_ratio: string;
  target_duration_sec: number;
  scene_count: number;
  storyboard_done_count: number;
  clip_done_count: number;
  thumbnail_url: string | null;
  final_video_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface MovieProjectDetail {
  id: string;
  title: string;
  concept: string | null;
  style_description: string | null;
  aspect_ratio: string;
  target_duration_sec: number;
  video_model: MovieVideoModel;
  image_model: string;
  status: MovieProjectStatus;
  final_video_url: string | null;
  concat_error_message: string | null;
  concatenated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface MovieSceneStoryboard {
  status: SceneStageStatus;
  job_id: string | null;
  error_message: string | null;
  image: { id: number; url: string; s3_key: string } | null;
}

export interface MovieSceneClip {
  status: SceneStageStatus;
  job_id: string | null;
  progress_percentage: number | null;
  progress_step: string | null;
  error_message: string | null;
  video: { id: number; url: string; duration_seconds: number } | null;
}

export interface MovieScene {
  id: string;
  display_order: number;
  summary: string | null;
  duration_sec: number;
  storyboard_prompt: string | null;
  clip_prompt: string | null;
  storyboard: MovieSceneStoryboard;
  clip: MovieSceneClip;
}

export interface MovieProjectData {
  project: MovieProjectDetail;
  scenes: MovieScene[];
}

export interface SceneInput {
  id?: string;
  summary?: string | null;
  duration_sec: number;
  storyboard_prompt?: string | null;
  clip_prompt?: string | null;
}

/** 絵コンテからクリップを生成できる、ムービー制作の選択肢。 */
export const MOVIE_VIDEO_MODELS = [
  'seedance-2-mini',
  'seedance-2-fast',
  'seedance-2-pro',
  'seedance-2-5',
  'fal-h3-max-turbo',
  'fal-h3-max',
] as const;

export type MovieVideoModel = (typeof MOVIE_VIDEO_MODELS)[number];

export interface CreateProjectParams {
  title: string;
  concept?: string;
  style_description?: string;
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  target_duration_sec?: number;
  video_model?: MovieVideoModel;
  image_model?: 'gpt-image-2' | 'nanobanana2';
}

const extractErrorMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || 'エラーが発生しました';

const movieService = {
  async getProjects(): Promise<MovieProjectSummary[]> {
    const response = await axiosInstance.get('/movies');
    return response.data.data.data; // paginate の data
  },

  async createProject(params: CreateProjectParams): Promise<string> {
    const response = await axiosInstance.post('/movies', params);
    return response.data.data.id;
  },

  async getProject(id: string): Promise<MovieProjectData> {
    const response = await axiosInstance.get(`/movies/${id}`);
    return response.data.data;
  },

  async updateProject(id: string, params: Partial<CreateProjectParams>): Promise<void> {
    await axiosInstance.patch(`/movies/${id}`, params);
  },

  async deleteProject(id: string): Promise<void> {
    await axiosInstance.delete(`/movies/${id}`);
  },

  /** AIで台本（シーン構成）を生成。既存シーンは置き換えられる */
  async generateScript(id: string): Promise<MovieProjectData> {
    const response = await axiosInstance.post(`/movies/${id}/generate-script`);
    return response.data.data;
  },

  /** シーンの一括更新（編集・並べ替え・追加・削除） */
  async updateScenes(id: string, scenes: SceneInput[]): Promise<MovieProjectData> {
    const response = await axiosInstance.put(`/movies/${id}/scenes`, { scenes });
    return response.data.data;
  },

  async generateStoryboard(projectId: string, sceneId: string): Promise<void> {
    await axiosInstance.post(`/movies/${projectId}/scenes/${sceneId}/storyboard`);
  },

  async generateClip(projectId: string, sceneId: string): Promise<void> {
    await axiosInstance.post(`/movies/${projectId}/scenes/${sceneId}/clip`);
  },

  async concat(projectId: string): Promise<void> {
    await axiosInstance.post(`/movies/${projectId}/concat`);
  },

  extractErrorMessage,
};

export default movieService;
